import {
  Card,
  ColorSwatch,
  Field,
  makeStyles,
  mergeClasses,
  Subtitle1,
  SwatchPicker,
  SwatchPickerOnSelectEventHandler,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircleFilled } from '@fluentui/react-icons';
import { ReactNode } from 'react';
import { ControlledTooltip } from './ControlledTooltip';
import classNames from 'classnames';

type TSwatchOption = {
  value: string;
  title: ReactNode;
  subtitle?: ReactNode;
  tooltip?: string;
  disabled?: boolean;
  onClick?: () => void;
};

export type TOptionModeLocked = {
  isModeLocked: boolean;
  // `message` is used to explain why the mode is locked in a tooltip or a dialog.
  lockedModeMessage?: string;
};

export type TOptionSwatchProps = {
  label?: string;
  options: TSwatchOption[];
  selectedValue?: string;
  setSelectedValue?: (selectedValue: string) => void;
  modeLocked?: TOptionModeLocked;
  className?: string;
};

const useStyles = makeStyles({
  card: {
    padding: 0,
    marginRight: tokens.spacingHorizontalM,
  },
  swatch: {
    width: '150px',
    height: '150px',

    '&:disabled': {
      opacity: 0.5,
    },
  },
  swatchText: {
    maxWidth: '132px',
  },
  check: {
    color: tokens.colorBrandBackground,
  },
});

const OptionSwatch = ({
  label,
  options,
  selectedValue,
  setSelectedValue,
  modeLocked,
  className = '',
}: TOptionSwatchProps) => {
  const handleSelectionChange: SwatchPickerOnSelectEventHandler = (_, data) => {
    setSelectedValue?.(data.selectedValue);
    options.find((option) => option.value === data.selectedValue)?.onClick?.();
  };

  const isNotSelectedValue = (option: TSwatchOption) => option.value !== selectedValue;

  const getTooltipContent = (option: TSwatchOption) => {
    if (modeLocked?.isModeLocked && modeLocked.lockedModeMessage && isNotSelectedValue(option))
      return modeLocked.lockedModeMessage;
    return option.tooltip || '';
  };

  // selectedValue || '#' is used as the SwatchPicker otherwise would become uncontrolled
  const classes = useStyles();
  return (
    <Field label={label} className={className}>
      <SwatchPicker selectedValue={selectedValue || '#'} onSelectionChange={handleSelectionChange} className="mt-1">
        {options.map((option) => (
          <ControlledTooltip
            key={option.value}
            enable={Boolean(getTooltipContent(option))}
            relationship="inaccessible"
            withArrow
            content={getTooltipContent(option)}
          >
            <Card className={classes.card}>
              <ColorSwatch
                color="transparent"
                value={option.value}
                shape="rounded"
                size="large"
                className={classes.swatch}
                disabled={(modeLocked?.isModeLocked && isNotSelectedValue(option)) || option.disabled}
                disabledIcon={null}
              >
                <div
                  className={classNames('flex flex-col items-center space-y-2', {
                    'pt-2': setSelectedValue,
                  })}
                >
                  <div className={mergeClasses('space-y-1', classes.swatchText)}>
                    <Subtitle1 className="!text-center">{option.title}</Subtitle1>
                    {option.subtitle && <p>{option.subtitle}</p>}
                  </div>
                  {setSelectedValue && (
                    <CheckmarkCircleFilled
                      fontSize="36px"
                      className={option.value === selectedValue ? classes.check : 'text-transparent'}
                    />
                  )}
                </div>
              </ColorSwatch>
            </Card>
          </ControlledTooltip>
        ))}
      </SwatchPicker>
    </Field>
  );
};

export { OptionSwatch };
